import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import {
  readCachedNextTripWidgetPayload,
  readCachedTripSurfaceSnapshot,
  type NextTripWidgetPayload,
} from '@/services/appleWidgetV1';
import type { AppleTripSurfaceSnapshotV1 } from '@/services/applePlatformSnapshot';

type FlagRow = {
  key: string;
  enabled: boolean;
};

export function ApplePhase5DebugCard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [widgetPayload, setWidgetPayload] = useState<NextTripWidgetPayload | null>(null);
  const [surfaceSnapshot, setSurfaceSnapshot] = useState<AppleTripSurfaceSnapshotV1 | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const flags = useMemo<FlagRow[]>(
    () => [
      { key: 'phase5Enabled', enabled: applePlatformFlags.phase5Enabled },
      { key: 'universalLinksEnabled', enabled: applePlatformFlags.universalLinksEnabled },
      { key: 'widgetNextTripEnabled', enabled: applePlatformFlags.widgetNextTripEnabled },
      { key: 'widgetDayBriefingEnabled', enabled: applePlatformFlags.widgetDayBriefingEnabled },
      { key: 'spotlightEnabled', enabled: applePlatformFlags.spotlightEnabled },
      { key: 'appIntentsReadActionsEnabled', enabled: applePlatformFlags.appIntentsReadActionsEnabled },
      { key: 'appIntentsWriteActionsEnabled', enabled: applePlatformFlags.appIntentsWriteActionsEnabled },
      { key: 'liveActivitiesEnabled', enabled: applePlatformFlags.liveActivitiesEnabled },
    ],
    [],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [payload, surface] = await Promise.all([
        readCachedNextTripWidgetPayload(),
        readCachedTripSurfaceSnapshot(),
      ]);
      setWidgetPayload(payload);
      setSurfaceSnapshot(surface);
    } finally {
      setLastCheckedAt(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Card variant="outlined" style={{ borderColor: theme.colors.warning + '66' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(1) }}>
        <Ionicons name="bug-outline" size={18} color={theme.colors.warning} />
        <SubHeading>Apple Phase 5 QA</SubHeading>
      </View>
      <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
        Runtime flags and latest cached Widget V1 payload.
      </Body>
      <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
        App Group: {applePlatformFlags.appGroupId || 'not configured'}
      </Caption>

      <View style={{ gap: theme.spacing(0.75), marginBottom: theme.spacing(1.5) }}>
        {flags.map((flag) => (
          <View key={flag.key} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Caption color={theme.colors.textSecondary}>{flag.key}</Caption>
            <ThemedText
              variant="caption"
              color={flag.enabled ? theme.colors.success : theme.colors.warning}
              style={{ fontWeight: '700' }}
            >
              {flag.enabled ? 'ON' : 'OFF'}
            </ThemedText>
          </View>
        ))}
      </View>

      <View
        style={{
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
          borderRadius: theme.radii.md,
          padding: theme.spacing(1.25),
          marginBottom: theme.spacing(1.25),
          backgroundColor: theme.colors.surface,
        }}
      >
        <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
          Cached Trip Surface Snapshot
        </Caption>
        {surfaceSnapshot ? (
          <Body style={{ fontSize: 12, lineHeight: 17 }}>
            {JSON.stringify(surfaceSnapshot, null, 2)}
          </Body>
        ) : (
          <Body color={theme.colors.textSecondary}>No trip surface snapshot cached yet.</Body>
        )}
      </View>

      <View
        style={{
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
          borderRadius: theme.radii.md,
          padding: theme.spacing(1.25),
          marginBottom: theme.spacing(1.25),
          backgroundColor: theme.colors.surface,
        }}
      >
        <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(0.5) }}>
          Cached Widget Payload
        </Caption>
        {widgetPayload ? (
          <Body style={{ fontSize: 12, lineHeight: 17 }}>
            {JSON.stringify(widgetPayload, null, 2)}
          </Body>
        ) : (
          <Body color={theme.colors.textSecondary}>No payload cached yet.</Body>
        )}
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Caption color={theme.colors.textSecondary}>
          {lastCheckedAt ? `Last refresh: ${lastCheckedAt.toLocaleTimeString()}` : 'Not refreshed yet'}
        </Caption>
        <Button
          label={loading ? 'Refreshing...' : 'Refresh'}
          size="small"
          variant="secondary"
          disabled={loading}
          onPress={() => void refresh()}
        />
      </View>
    </Card>
  );
}
